import {MenuBarView} from '@gongxigames/framework/slot/modules/menubar/MenuBarView';
import {MenuBarViewMobile} from '@gongxigames/framework/slot/modules/menubar/MenuBarViewMobile';
import {MenuBarButtonConfig} from '@gongxigames/framework/slot/modules/menubar/IMenuBar';
import {CommandLineInterface, CoreDI, DeviceType, inject} from '@gongxigames/framework/core';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';

// TODO: refactoring required
// @ts-ignore
export class MenuBarModule extends Module<MenuBarView|MenuBarViewMobile, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('MenuBarModule');
    public static platform: DeviceType = DeviceType.MOBILE;

    public static readonly BASIC_STATE: string = 'basic';

    @inject(CoreDI.CLI)
    private _cli!: CommandLineInterface;

    private _showSoundButton = true;
    private readonly _buttonsPanelConfig: MenuBarButtonConfig = {
        soundOn: {},
        soundOff: {},
        close: {},
        paytable: {},
        gameRules: {},
        gameHistory: {},
        cashier: {},
        support: {},
        logout: {}
    };

    public openMenuBar(): void {
        this.view.show();
        this.openInfoPageByDefault();
        strictUiEmitter.emit('UI.menu.opened');
        this.eventManager.emit('General.UI.DISABLE', MenuBarModule.NAME);
    }

    public closeMenuBar(): void {
        this.view.hide();
        strictUiEmitter.emit('UI.menu.closed');
        strictUiEmitter.emit('UI.menu.menuBarClosed');
        this.eventManager.emit('General.UI.ENABLE', MenuBarModule.NAME);
    }

    public openPaytable(): void {
        this.setActiveButton('paytable');
        strictUiEmitter.emit('UI.menu.closed');
        strictUiEmitter.emit('UI.menu.infoPageClicked');
    }

    public openGameRules(): void {
        this.setActiveButton('gameRules');
        strictUiEmitter.emit('UI.menu.closed');
        strictUiEmitter.emit('UI.menu.gameRulesClicked');
    }

    public openGameHistory(): void {
        if (this.settings.get('gameHistoryListEnabled')) {
            this.setActiveButton('gameHistory');
            strictUiEmitter.emit('UI.menu.closed');
        }

        strictUiEmitter.emit('UI.menu.historyButtonClicked');
    }

    protected setupViews(): void {
        const view = this.isPlatformDesktop() ? MenuBarView.NAME : MenuBarViewMobile.NAME;
        const {
            soundEnabled,
            soundsMuted,
            historyUrl,
            supportUrl,
            logoutUrl,
            cashierUrl,
            hideHistoryButton,
            hideSupportButton,
            hideLogoutButton,
            hideCashierButton,
            hideSoundCheckBox
        } = this.settings.getAllProperties();

        this.setView(view);

        this._buttonsPanelConfig.gameHistory.hide = !historyUrl || hideHistoryButton;
        this._buttonsPanelConfig.support.hide = !supportUrl || hideSupportButton;
        this._buttonsPanelConfig.logout.hide = !logoutUrl || hideLogoutButton;
        this._buttonsPanelConfig.cashier.hide = !cashierUrl || hideCashierButton;

        this._showSoundButton = soundEnabled && !hideSoundCheckBox;

        this.setSoundButton(!soundsMuted);
        this.updateButtonPanel();
    }

    protected setupStates(): void {}

    protected setupEvents(): void {
        super.setupEvents();

        strictUiEmitter.on('UI.sounds.switchedTo', this.setSoundButton.bind(this));
        this.on('General.Popups.Add', this.onMenuBarClosed);

        this.onView(MenuBarView.MENU_BAR_OPENED, this.onMenuBarOpen);

        this._buttonsPanelConfig.close.action = this.onMenuBarClosed.bind(this);
        this._buttonsPanelConfig.gameHistory.action = this.onGameHistoryClicked.bind(this);
        this._buttonsPanelConfig.gameRules.action = this.onGameRulesClicked.bind(this);
        this._buttonsPanelConfig.paytable.action = this.onPaytableClicked.bind(this);
        this._buttonsPanelConfig.soundOff.action = this.onSoundButtonClicked.bind(this, true);
        this._buttonsPanelConfig.soundOn.action = this.onSoundButtonClicked.bind(this, false);
        this._buttonsPanelConfig.support.action = this.onSupportClicked.bind(this);
        this._buttonsPanelConfig.cashier.action = this.onCashierClicked.bind(this);
        this._buttonsPanelConfig.logout.action = this.onLogoutClicked.bind(this);

        /* develblock:start */
        this._cli.addCommand('menuBar', {
            showGameRules: () => {
                this.onMenuBarOpen();
                this.onGameRulesClicked();
            },
            showInfoPage: () => {
                this.onMenuBarOpen();
                this.openInfoPageByDefault();
            },
            close: () => {
                this.onMenuBarClosed();
            }
        });
        /* develblock:end */
    }

    protected setSoundButton(isOn: boolean): void {
        if (this._showSoundButton) {
            this._buttonsPanelConfig.soundOn.hide = !isOn;
            this._buttonsPanelConfig.soundOff.hide = isOn;
        } else {
            this._buttonsPanelConfig.soundOn.hide = true;
            this._buttonsPanelConfig.soundOff.hide = true;
        }

        this.updateButtonPanel();
    }

    protected setActiveButton(button: 'gameRules'|'paytable'|'gameHistory'): void {
        this._buttonsPanelConfig.gameRules.active = false;
        this._buttonsPanelConfig.paytable.active = false;
        this._buttonsPanelConfig.gameHistory.active = false;

        this._buttonsPanelConfig[button].active = true;

        this.updateButtonPanel();
    }

    protected onMenuBarOpen(): void {
        this.soundPlayer.play('buttonclick');
        this.openMenuBar();
    }

    protected openInfoPageByDefault(): void {
        this.setActiveButton('paytable');
        strictUiEmitter.emit('UI.menu.infoPageClicked');
    }

    protected onSoundButtonClicked(isOn: boolean): void {
        this.soundPlayer.play('buttonclick');
        const event = isOn ? 'UI.sounds.enable' : 'UI.sounds.disable';

        this.setSoundButton(isOn);
        strictUiEmitter.emit(event);
    }

    protected onMenuBarClosed(): void {
        this.soundPlayer.play('buttonclick');
        this.closeMenuBar();
    }

    protected onPaytableClicked(): void {
        this.soundPlayer.play('buttonclick');
        this.openPaytable();
    }

    protected onGameRulesClicked(): void {
        this.soundPlayer.play('buttonclick');
        this.openGameRules();
    }

    protected onGameHistoryClicked(): void {
        this.soundPlayer.play('buttonclick');
        this.openGameHistory();
    }

    protected onSupportClicked(): void {
        this.soundPlayer.play('buttonclick');
        strictUiEmitter.emit('UI.menu.supportButtonClicked');
    }

    protected onCashierClicked(): void {
        this.soundPlayer.play('buttonclick');
        strictUiEmitter.emit('UI.menu.cashierClicked');
    }

    protected onLogoutClicked(): void {
        this.soundPlayer.play('buttonclick');
        strictUiEmitter.emit('UI.menu.logoutButtonClicked');
    }

    private updateButtonPanel(): void {
        this.view.updateButtonPanel(this._buttonsPanelConfig);
    }
}
